import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";

// Layout
import Layout from "./layouts/Layout";

// Pages
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import TemplatesPage from "./pages/TemplatesPage";
import SubmissionsListPage from "./pages/SubmissionsListPage";
import NewSubmissionPage from "./pages/NewSubmissionPage";
import SubmissionDetailPage from "./pages/SubmissionDetailPage";
import SOPsPage from "./pages/SOPsPage";
import SOPDetailPage from "./pages/SOPDetailPage";
import VehiclesPage from "./pages/VehiclesPage";
import DriversPage from "./pages/DriversPage";
import CorrectiveActionsPage from "./pages/CorrectiveActionsPage";
import ReportsPage from "./pages/ReportsPage";
import AuditLogPage from "./pages/AuditLogPage";
import FormDataPage from "./pages/FormDataPage";
import ServiceSchedulePage from "./pages/ServiceSchedulePage";
import TyreSurveyPage from "./pages/TyreSurveyPage";
import BreakdownsPage from "./pages/BreakdownsPage";
import AccidentsPage from "./pages/AccidentsPage";
import FineSchedulePage from "./pages/FineSchedulePage";
import FuelSchedulePage from "./pages/FuelSchedulePage";
import PlanningLoadingPage from "./pages/PlanningLoadingPage";
import ReplacementPlanPage from "./pages/ReplacementPlanPage";
import RiskManagementPage from "./pages/RiskManagementPage";
import BreathalyzerPage from "./pages/BreathalyzerPage";
import TelematicsPage from "./pages/TelematicsPage";
import ComplaintsPage from "./pages/ComplaintsPage";
import RecruitmentPage from "./pages/RecruitmentPage";
import TrainingPage from "./pages/TrainingPage";
import DailyInspectionsPage from "./pages/DailyInspectionsPage";
import UserManualPage from "./pages/UserManualPage";
import SettingsPage from "./pages/SettingsPage";
import SchedulesTemplatesPage from "./pages/SchedulesTemplatesPage";

// Protected Route Component
const ProtectedRoute = ({ children, loginPath = "/login" }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={loginPath} replace />;
  }

  return <Layout>{children}</Layout>;
};

// Public Route Component
const PublicRoute = ({ children, dashboardPath = "/dashboard" }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to={dashboardPath} replace />;
  }

  return children;
};

// All app routes, parameterized by prefix
function AppRouteSet({ prefix = "" }) {
  const loginPath = `${prefix}/login`;
  const dashPath = `${prefix}/dashboard`;
  const P = ({ children }) => <ProtectedRoute loginPath={loginPath}>{children}</ProtectedRoute>;
  const Pub = ({ children }) => <PublicRoute dashboardPath={dashPath}>{children}</PublicRoute>;

  return (
    <Routes>
      <Route path={`${prefix}/login`} element={<Pub><LoginPage /></Pub>} />
      <Route path={`${prefix}/dashboard`} element={<P><DashboardPage /></P>} />
      <Route path={`${prefix}/templates`} element={<P><TemplatesPage /></P>} />
      <Route path={`${prefix}/form-data`} element={<P><FormDataPage /></P>} />
      <Route path={`${prefix}/form-data/:templateCode`} element={<P><FormDataPage /></P>} />
      <Route path={`${prefix}/submissions`} element={<P><SubmissionsListPage /></P>} />
      <Route path={`${prefix}/submissions/new/:templateCode`} element={<P><NewSubmissionPage /></P>} />
      <Route path={`${prefix}/submissions/:submissionId`} element={<P><SubmissionDetailPage /></P>} />
      <Route path={`${prefix}/sops`} element={<P><SOPsPage /></P>} />
      <Route path={`${prefix}/sops/:sopCode`} element={<P><SOPDetailPage /></P>} />
      <Route path={`${prefix}/vehicles`} element={<P><VehiclesPage /></P>} />
      <Route path={`${prefix}/drivers`} element={<P><DriversPage /></P>} />
      <Route path={`${prefix}/corrective-actions`} element={<P><CorrectiveActionsPage /></P>} />
      <Route path={`${prefix}/service-schedule`} element={<P><ServiceSchedulePage /></P>} />
      <Route path={`${prefix}/tyre-survey`} element={<P><TyreSurveyPage /></P>} />
      <Route path={`${prefix}/breakdowns`} element={<P><BreakdownsPage /></P>} />
      <Route path={`${prefix}/accidents`} element={<P><AccidentsPage /></P>} />
      <Route path={`${prefix}/fines`} element={<P><FineSchedulePage /></P>} />
      <Route path={`${prefix}/fuel-schedule`} element={<P><FuelSchedulePage /></P>} />
      <Route path={`${prefix}/planning`} element={<P><PlanningLoadingPage /></P>} />
      <Route path={`${prefix}/replacement-plan`} element={<P><ReplacementPlanPage /></P>} />
      <Route path={`${prefix}/risk-management`} element={<P><RiskManagementPage /></P>} />
      <Route path={`${prefix}/breathalyzer`} element={<P><BreathalyzerPage /></P>} />
      <Route path={`${prefix}/telematics`} element={<P><TelematicsPage /></P>} />
      <Route path={`${prefix}/complaints`} element={<P><ComplaintsPage /></P>} />
      <Route path={`${prefix}/recruitment`} element={<P><RecruitmentPage /></P>} />
      <Route path={`${prefix}/training`} element={<P><TrainingPage /></P>} />
      <Route path={`${prefix}/daily-inspections`} element={<P><DailyInspectionsPage /></P>} />
      <Route path={`${prefix}/reports`} element={<P><ReportsPage /></P>} />
      <Route path={`${prefix}/audit-log`} element={<P><AuditLogPage /></P>} />
      <Route path={`${prefix}/user-manual`} element={<P><UserManualPage /></P>} />
      <Route path={`${prefix}/settings`} element={<P><SettingsPage /></P>} />
      <Route path={`${prefix}/schedules-templates`} element={<P><SchedulesTemplatesPage /></P>} />
      {/* Default redirect */}
      {prefix === "" && <Route path="/" element={<Navigate to="/dashboard" replace />} />}
      {prefix === "" && <Route path="*" element={<Navigate to="/dashboard" replace />} />}
      {prefix === "/test" && <Route path="/test" element={<Navigate to="/test/dashboard" replace />} />}
      {prefix === "/test" && <Route path="/test/*" element={<Navigate to="/test/dashboard" replace />} />}
    </Routes>
  );
}

// Environment-aware wrapper
function EnvironmentRouter() {
  const location = useLocation();
  const isTest = location.pathname.startsWith('/test');
  const env = isTest ? 'test' : 'live';

  return (
    <AuthProvider environment={env} key={env}>
      {isTest ? <AppRouteSet prefix="/test" /> : <AppRouteSet prefix="" />}
    </AuthProvider>
  );
}

function App() {
  React.useEffect(() => {
    const handler = (e) => toast.error(e.detail || 'Download failed');
    window.addEventListener('download-error', handler);
    return () => window.removeEventListener('download-error', handler);
  }, []);

  return (
    <BrowserRouter>
      <EnvironmentRouter />
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  );
}

export default App;
